import { v4 as uuidv4 } from 'uuid';

import {
  sendOperationStorageKey,
  type SendOperationKind,
} from '../../utils/organizationStorage';

export type { SendOperationKind };

type PersistedSendOperationIdentity = {
  version: 1;
  clientMessageId: string;
  sourceKey: string;
  sourceRevision: number;
  intentHash: string;
  currentDraftId?: string;
  /**
   * この操作を始めた利用者。logoutではlocalStorageを消さないため、
   * 共有PCで別の利用者がログインすると前任者のsource keyを引き継いでしまう。
   * 所有者を証明できない値は持ち越さないので必須にする。
   */
  ownerKey: string;
};

const UUID_V4_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export const SEND_OPERATION_CRYPTO_UNAVAILABLE_MESSAGE =
  'このブラウザでは安全な送信操作を開始できません。ブラウザを最新版に更新してください。';

export const SEND_OPERATION_OWNER_UNAVAILABLE_MESSAGE =
  '送信操作の利用者を確認できません。再度ログインしてからお試しください。';

function getStorage(): Storage | null {
  try {
    return typeof window === 'undefined' ? null : window.localStorage;
  } catch {
    return null;
  }
}

function readPersistedIdentity(
  kind: SendOperationKind,
  ownerKey: string,
): PersistedSendOperationIdentity | null {
  const raw = getStorage()?.getItem(sendOperationStorageKey(kind, ownerKey));
  if (!raw) return null;

  try {
    const value = JSON.parse(raw) as Partial<PersistedSendOperationIdentity>;
    if (
      value.version !== 1 ||
      typeof value.clientMessageId !== 'string' ||
      !UUID_V4_PATTERN.test(value.clientMessageId) ||
      typeof value.sourceKey !== 'string' ||
      value.sourceKey.length === 0 ||
      !Number.isInteger(value.sourceRevision) ||
      Number(value.sourceRevision) <= 0 ||
      typeof value.intentHash !== 'string' ||
      value.intentHash.length !== 64 ||
      (value.currentDraftId !== undefined &&
        typeof value.currentDraftId !== 'string') ||
      typeof value.ownerKey !== 'string' ||
      value.ownerKey.length === 0
    ) {
      return null;
    }
    return value as PersistedSendOperationIdentity;
  } catch {
    return null;
  }
}

function writePersistedIdentity(
  kind: SendOperationKind,
  identity: PersistedSendOperationIdentity,
): void {
  getStorage()?.setItem(
    sendOperationStorageKey(kind, identity.ownerKey),
    JSON.stringify(identity),
  );
}

async function withCrossTabLock<T>(
  kind: SendOperationKind,
  callback: () => Promise<T> | T,
): Promise<T> {
  if (typeof navigator !== 'undefined' && navigator.locks) {
    return navigator.locks.request(
      `line-ban:send-operation:${kind}`,
      callback,
    );
  }
  return callback();
}

function canonicalJson(value: unknown): string {
  const normalized = JSON.parse(JSON.stringify(value)) as unknown;
  const sortValue = (current: unknown): unknown => {
    if (Array.isArray(current)) return current.map(sortValue);
    if (current && typeof current === 'object') {
      return Object.fromEntries(
        Object.keys(current as Record<string, unknown>)
          .sort()
          .map((key) => [
            key,
            sortValue((current as Record<string, unknown>)[key]),
          ]),
      );
    }
    return current;
  };
  return JSON.stringify(sortValue(normalized));
}

/** backendと同じcanonical JSONからintentHashを生成し、本文自体は保存しない。 */
export async function createLineDispatchIntentHash(
  value: unknown,
): Promise<string> {
  const serialized = canonicalJson(value);
  const webCrypto =
    typeof window === 'undefined' ? globalThis.crypto : window.crypto;
  const subtle = webCrypto?.subtle;
  if (
    !subtle ||
    typeof subtle.digest !== 'function' ||
    typeof TextEncoder === 'undefined'
  ) {
    // backendと異なるdigestでintent照合を続けると、response loss後に同一操作を
    // 新規送信と誤認し得る。SHA-256を生成できない環境では必ず送信を止める。
    throw new Error(SEND_OPERATION_CRYPTO_UNAVAILABLE_MESSAGE);
  }

  const bytes = new TextEncoder().encode(serialized);
  const digest = await subtle.digest('SHA-256', bytes);
  return Array.from(new Uint8Array(digest), (byte) =>
    byte.toString(16).padStart(2, '0'),
  ).join('');
}

/**
 * reload・別tabと共有するsource identityを原子的にclaimする。
 * 同一payloadなら保存済みUUIDを返し、異なる操作だけ新しいUUIDを発行する。
 */
export async function claimSendOperationIdentity(params: {
  kind: SendOperationKind;
  intentHash: string;
  sourceRevision: number;
  ownerKey: string;
  sourceKeyOverride?: string;
  preferredClientMessageId?: string;
}): Promise<PersistedSendOperationIdentity> {
  if (params.ownerKey.length === 0) {
    // ownerを綴じられないidentityはresponse loss後に自分の操作だと証明できず、
    // 回復も再送判定もできない。送信を始める前に必ず止める。
    throw new Error(SEND_OPERATION_OWNER_UNAVAILABLE_MESSAGE);
  }
  return withCrossTabLock(params.kind, () => {
    const persisted = readPersistedIdentity(params.kind, params.ownerKey);
    // 成功/取消を確認していないoperationはpayload変更でも上書きしない。
    // response loss後の再upload URL変化で新UUIDを発行すると二重配信になる。
    // ただし別の利用者が残したidentityは引き継がない。前任者のsource keyで
    // 送るとbackendが所有者不一致で409を返し、その利用者は送信できなくなる。
    // ownerを証明できない値(旧形式・改竄)はreadPersistedIdentityがnullにする。
    if (persisted && persisted.ownerKey === params.ownerKey) {
      if (persisted.intentHash === params.intentHash) return persisted;
      // sourceKey/UUIDは二重配信防止の錨として据え置くが、intentHashは
      // 「今まさに送る本文」を指し続けないとrecoveryのD22照合が初回claim時の
      // 本文と比較してしまい、別内容のCOMPLETED dispatchを回収して
      // 未送信のまま成功表示する(逆方向は恒久的なsource key不一致409)。
      const updated: PersistedSendOperationIdentity = {
        ...persisted,
        intentHash: params.intentHash,
      };
      writePersistedIdentity(params.kind, updated);
      return updated;
    }

    const clientMessageId =
      params.preferredClientMessageId ?? uuidv4();
    const identity: PersistedSendOperationIdentity = {
      version: 1,
      clientMessageId,
      sourceKey: params.sourceKeyOverride ?? clientMessageId,
      sourceRevision: params.sourceRevision,
      intentHash: params.intentHash,
      ownerKey: params.ownerKey,
      ...(params.sourceKeyOverride
        ? { currentDraftId: params.sourceKeyOverride }
        : {}),
    };
    writePersistedIdentity(params.kind, identity);
    return identity;
  });
}

/**
 * 同じclient送信IDへ別内容を送ってbackendが拒否した応答かを判定する。
 * この409だけはproviderへ何も送られていないので、identityを捨てて
 * 次の送信を新しいIDで始めてよい。
 * 例: 将来予約の応答を失った後に別内容を送ると、予約が同じIDを占有したまま
 * 拒否され続け、そのブラウザから一斉送信できなくなる。
 * @param error 送信APIが投げたerror
 * @returns 内容不一致の409ならtrue
 */
export function isSendOperationPayloadMismatch(error: unknown): boolean {
  if (typeof error !== 'object' || error === null) return false;
  const response = (error as { response?: { data?: unknown } }).response;
  const data = response?.data;
  if (typeof data !== 'object' || data === null) return false;
  return (
    (data as { code?: unknown }).code === 'BULK_SEND_PAYLOAD_MISMATCH'
  );
}

/** 成功応答を確認した操作だけを消し、別tabが作った新しいidentityは保持する。 */
export function clearSendOperationIdentity(
  kind: SendOperationKind,
  ownerKey: string,
  clientMessageId: string,
): void {
  const persisted = readPersistedIdentity(kind, ownerKey);
  if (persisted?.clientMessageId === clientMessageId) {
    getStorage()?.removeItem(sendOperationStorageKey(kind, ownerKey));
  }
}

/** backend正本から復旧した同一intentのlocal補助identityを消す。 */
export function clearSendOperationIdentityByIntent(
  kind: SendOperationKind,
  ownerKey: string,
  intentHash: string,
): void {
  const persisted = readPersistedIdentity(kind, ownerKey);
  if (persisted?.intentHash === intentHash) {
    getStorage()?.removeItem(sendOperationStorageKey(kind, ownerKey));
  }
}
